import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  TableCell, TableRow, IconButton, Box,
  Modal, Tooltip,
  Typography
} from "@mui/material";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { PERMISSIONS } from "../../constants";
import { getContractField } from "../../functions";

const style = {
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  left: '50%',
  p: 4,
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
};

const statusColor = {
  DRAFT: 'gray',
  SUPERVISOR: 'orange',
  APPROVED: 'green',
  SUSPENDED: '#03a9f4',
  FINISHED: '#bbb810ff',
  CLOSED: 'red',
}

// `showPersonType` viene de la lista: la columna solo se dibuja cuando el
// usuario ve naturales Y jurídicos. Con una sola clase la columna repetiría el
// mismo valor en cada fila.
const ContractItem = ({ contract, showPersonType = true }) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const [open, setOpen] = useState(false);

  // Contratos de personas jurídicas (colección contractsLegal): mismo listado,
  // rutas y supervisor propios (rol SUPERVISOR_LEGAL_CONTRACT).
  const isLegal = contract.personType === 'JURIDICA';
  const baseRoute = isLegal ? `/contracts/legal/${contract.contractCode}` : `/contracts/${contract.contractCode}`;

  // El rol de apoyo también porta SUPERVISOR_CONTRACT.UPDATE: edita los
  // contratos donde está asignado con el mismo alcance del supervisor,
  // sin importar el estado; para supervisores y admins no cambia nada.
  const canEdit = user.permissions.includes(PERMISSIONS.CONTRACT.UPDATE)
    || (user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.UPDATE)
      && (user.roles.includes(isLegal ? 'SUPERVISOR_LEGAL_CONTRACT' : 'SUPERVISOR_CONTRACT')
        || contract.supportIDs?.includes(user.userId)));

  return (
    <>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box sx={style}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" component="h2" sx={{ flex: 1 }}>Objeto del contrato</Typography>
            <CloseIcon sx={{ cursor: 'pointer' }} onClick={() => setOpen(false)} />
          </Box>

          <Typography sx={{ mt: 2 }}>
            {contract.contractSubject}
          </Typography>
        </Box>
      </Modal>
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: statusColor[
                  getContractField(contract.news, contract)('status')
                ],
                mr: 1,
                borderRadius: '50%'
              }}
            />
            {contract.contractCode}
          </Box>
        </TableCell>
        {showPersonType
          && <TableCell>
            {/* Los naturales legados no tienen personType (sin backfill). */}
            {isLegal ? 'Persona Jurídica' : 'Persona Natural'}
          </TableCell>
        }
        <TableCell>
          {getContractField(contract.news, contract)('supervisors')?.length ? (
            getContractField(contract.news, contract)('supervisors').map(s => (<div key={s.userId}>{s.name} {s.lastName}</div>))
          ) : (
            <Typography color='orange'>Sin supervisor</Typography>
          )}
        </TableCell>
        <TableCell>
          {isLegal ? (
            contract.entity?.legalName || <Typography color='#d19435'>Sin contratista</Typography>
          ) : contract.contractor ? (
            `${contract.contractor.name} ${contract.contractor.lastName}`
          ) : (
            <Typography color='#d19435'>Sin contratista</Typography>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setOpen(true)}>
              <DescriptionIcon fontSize="small" color="info" />
            </IconButton>
            <Box sx={{ marginLeft: '0.5em'}}>{contract.contractSubject.substr(0, 100)}</Box>
          </Box>
        </TableCell>
        <TableCell>
          {getContractField(contract.news, contract)('contractPeriod')}
        </TableCell>
        <TableCell>
          {getContractField(contract.news, contract)('finishedAt')?.isValid?.()
            ? getContractField(contract.news, contract)('finishedAt').format('DD/MM/YYYY')
            : '-'}
        </TableCell>
        <TableCell>
          <Box style={{ flexDirection: 'row', alignItems: 'center', minWidth: 120 }}>
            {/* <IconButton onClick={() => window.open(contract.fileUrl, '_blank')}>
              <OpenInNewIcon size={20} />
            </IconButton> */}
            {canEdit
              && <IconButton onClick={() => window.open(baseRoute, '_blank')}>
                <ModeEditIcon size={20} />
              </IconButton>
            }
            {contract.status === 'APPROVED' && user.permissions.includes(PERMISSIONS.CONTRACT.READ)
              && <IconButton onClick={() => navigate(`${baseRoute}/view`)}>
                <DescriptionIcon size={20} />
              </IconButton>
            }
            {/* Solicitudes de pago (C5): solo jurídicos y solo con el contrato
                aprobado, que es cuando admite cobro. Quién puede ver o escribir
                cada solicitud lo resuelve la Cloud Function. */}
            {isLegal && contract.status === 'APPROVED'
              && <Tooltip title="Solicitudes de pago">
                <IconButton onClick={() => navigate(`${baseRoute}/payments`)}>
                  <ReceiptLongIcon size={20} />
                </IconButton>
              </Tooltip>
            }
          </Box>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ContractItem;
